/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import React from "react";
import { useState, useEffect } from 'react'
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError, InteractionStatus } from "@azure/msal-browser";

import { Navbar, Container, Alert } from "react-bootstrap";

import { loginRequest, tokenRequest } from "../authConfig";

import { Grid } from "./grid/Grid"
import { Keyboard } from './keyboard/Keyboard'

import { getAPI, postAPI } from '../lib/api'
import { IUser, PointLedgerItemType } from "../interfaces/IGameInterfaces";

import { BoardSteps } from "./boardsteps";

import { IIconProps, Spinner, SpinnerSize } from '@fluentui/react';
import { IconButton } from '@fluentui/react/lib/Button';
import { Help } from './help';
import { GameComplete } from "./gamecomplete";
import { WORD_NOT_FOUND_MESSAGE, WIN_MESSAGES } from "../constants/messages";
import { REVEAL_TIME_MS } from "../constants/settings";
import { useAppInsightsContext, useTrackEvent, useTrackMetric } from "@microsoft/applicationinsights-react-js";
import { useNavigate } from "react-router-dom";
import { AdGameComplete } from './ads/AdGameComplete'


const ALERT_TIME_MS = 5000

export const Home = (props) => {
    const appInsightsContext = useAppInsightsContext();
    const navigate = useNavigate();

    const signoutIcon: IIconProps = { iconName: 'SignOut' };
    const helpIcon: IIconProps = { iconName: 'Help' };

    const [colorScheme, setColorScheme] = useState(props.colorScheme);

    const [gridSize, setGridSize] = useState(4)
    const [currentGuess, setCurrentGuess] = useState('')
    const [user, setUser] = useState<IUser | null>(null);
    const [accessToken, setAccessToken] = useState('');
    const [showHelp, setShowHelp] = useState(props.showHelp);
    const [isBanned, setIsBanned] = useState(false);
    const [isGameLoaded, setIsGameLoaded] = useState(false);
    const [isGameComplete, setIsGameComplete] = useState(false)
    const [isWordFound, setIsWordFound] = useState(false)
    const [isBoardComplete, setIsBoardComplete] = useState(false)

    const [currentRowClass, setCurrentRowClass] = useState('')

    const [errorMessage, setErrorMessage] = useState('');

    const [points, setPoints] = useState<JSX.Element[]>([]);


    const { instance, accounts, inProgress } = useMsal();

    const trackComponent = useTrackMetric(appInsightsContext,"PageLayout");
    const trackBoardComplete = useTrackEvent(appInsightsContext, "Board Complete", user);
    const trackGameComplete = useTrackEvent(appInsightsContext, "Game Complete", user);

    useEffect(() => {
        setColorScheme(props.colorScheme);
    },[props]);

    useEffect(() => {
        const request = {
            ...tokenRequest,
            account: accounts[0]
        };
        if (!user && inProgress === InteractionStatus.None) {
            instance.acquireTokenSilent(request).then((accessTokenResponse) => {
                setAccessToken(accessTokenResponse.accessToken);

                getAPI('users', accessTokenResponse.accessToken, (response) => {
                    if (response) {
                        setUser(response);
                    }}, (error) => {
                        if (error.message === "Failed to fetch") {
                            setErrorMessage("Unable to connect to the Words 5X server to initiate your game. Please try again later!");
                        } else {
                            setErrorMessage(error.message);
                        }
                    });
            }).catch((error) => {
                if (error instanceof InteractionRequiredAuthError) {
                    instance.acquireTokenRedirect(request);
                }
                console.log(error);
            });
        }
    }, [accounts, inProgress]);

    useEffect(() => {
        if (user !== undefined && user !== null )
        {
            if (user.banned)
            {
                setIsBanned(true);
                setIsGameLoaded(false);
                setErrorMessage("This account has been banned.");
            } else {
                props.appInsights.setAuthenticatedUserContext(user.id, user.id, true);
                setIsGameLoaded(true);

                setGridSize((user?.currentGame?.currentBoard ?? 0) + 4);
                setCurrentGuess('');

                if (user!.currentGame.isRevealing)
                {
                    setTimeout(() => {
                        user!.currentGame.isRevealing = false;
                        setUser(user);
                    }, REVEAL_TIME_MS * gridSize);
                }

                if (user?.currentGame?.complete ?? false) {
                    setIsGameComplete(true);
                    setIsBoardComplete(true);
                    
                    var boardpoints = user?.currentGame?.boards[user?.currentGame?.currentBoard]?.pointsLedger.filter(function (item) {
                        return item.type === PointLedgerItemType.BoardPoints;
                    }) ?? [];

                    const temp: JSX.Element[] = [];

                    for (var i = 0; i < boardpoints.length; i++) {
                        temp.push(<p key={i}>{boardpoints[i].description} +{boardpoints[i].value} pts</p>);
                    }

                    setPoints(temp);

                    if (user?.currentGame?.boards[user?.currentGame?.currentBoard]?.found ?? false) {
                        setIsWordFound(true);
                    } else {
                        setIsWordFound(false);
                    }

                    setTimeout(() => {
                        setIsBoardComplete(false);
                        setIsWordFound(false);
                    }, 6500);
                    trackGameComplete(user);
                } else {
                    setIsGameComplete(false);
                    if (user?.currentGame?.boards[user?.currentGame?.currentBoard]?.complete ?? false) {
                        setIsBoardComplete(true);

                        var boardpoints = user?.currentGame?.boards[user?.currentGame?.currentBoard]?.pointsLedger.filter(function (item) {
                            return item.type === PointLedgerItemType.BoardPoints;
                        }) ?? [];

                        const temp: JSX.Element[] = [];

                        for (var i = 0; i < boardpoints.length; i++) {
                            temp.push(<p key={i}>{boardpoints[i].description} +{boardpoints[i].value} pts</p>);
                        }

                        setPoints(temp);

                        if (user?.currentGame?.boards[user?.currentGame?.currentBoard]?.found ?? false) {
                            setIsWordFound(true);
                        } else {
                            setIsWordFound(false);
                        }
                        trackBoardComplete(user);
                    } else {
                        setIsBoardComplete(false);
                        setIsWordFound(false);
                    }
                }
            }
        }
    }, [user]);

    useEffect(() => {
        if ((errorMessage !== '') && (isGameLoaded)) {
            setCurrentRowClass('jiggle');
            setTimeout(() => {
                setErrorMessage('');
                setCurrentRowClass('');
            }, ALERT_TIME_MS);
        }
    }, [errorMessage]);

    const onNextBoard = () => {
        const request = {
            ...tokenRequest,
            account: accounts[0]
        };
        if (inProgress === InteractionStatus.None) {
            instance.acquireTokenSilent(request).then((accessTokenResponse) => {
                setAccessToken(accessTokenResponse.accessToken);

                getAPI('games/nextboard', accessTokenResponse.accessToken ,(response) => {
                    if (response) {
                        setUser(response);
                    }}, (error) => {
                        console.log(error);
                        setErrorMessage(error.message);
                    });
                        
            }).catch((error) => {
                if (error instanceof InteractionRequiredAuthError) {
                    instance.acquireTokenRedirect(request);
                }
                console.log(error);
            });
        }

        trackComponent();

    }

    const onChar = (value: any) => {
        if ((currentGuess.length < gridSize) && !(isBoardComplete) && !(isGameComplete)) {
            setCurrentGuess(`${currentGuess}${value}`)
            trackComponent();
        }
    }

    const onDelete = () => {
        if (!(isBoardComplete) && !(isGameComplete)) {
            setCurrentGuess(currentGuess.slice(0, -1))
            trackComponent();
        }
    }

    const onEnter = () => {
        if (isBoardComplete || isGameComplete) {
            return
        }
        if (!(currentGuess.length === gridSize)) {
            setErrorMessage("Not enough letters!");
            return
        }

        var data = new FormData();

        data.append("GUESS", currentGuess);

        const request = {
            ...tokenRequest,
            account: accounts[0]
        };
        if (inProgress === InteractionStatus.None) {
            instance.acquireTokenSilent(request).then((accessTokenResponse) => {
                setAccessToken(accessTokenResponse.accessToken);

                postAPI('games/guess', accessTokenResponse.accessToken, data, (response: IUser) => {
                    if (response) {
                        response.currentGame.isRevealing = true;
                        setUser(response);
                    }}, (error) => {
                        if (error.message === "404") {
                            setErrorMessage(WORD_NOT_FOUND_MESSAGE);
                        } else {
                            console.log(error.message);
                            setErrorMessage(error.message);
                        }
                    });
                
            }).catch((error) => {
                if (error instanceof InteractionRequiredAuthError) {
                    instance.acquireTokenRedirect(request);
                }
                console.log(error);
            });
        }

        trackComponent();

    }


    return (
    <div className="main-container">
        <div className="main-content">
            <Navbar className="navbar navbar-expand navbar-light navbar-bg">
                <Container>
                    <Navbar.Brand href="/"><img src={colorScheme === "dark" ? "/images/headerlogo-nightmode.png" : "/images/headerlogo.png"} alt="Words 5X" /></Navbar.Brand>
                    <div className="text-right">
                        <UnauthenticatedTemplate>
                            <div>
                                <button id="btnPlaynow" className="btn btn-primary" onClick={() => window.location.href="#signin"}>Play Now!</button>
                            </div>
                        </UnauthenticatedTemplate>
                        <AuthenticatedTemplate>
                            {(isGameLoaded) && (
                                <div>
                                    <IconButton iconProps={helpIcon} title="Help" ariaLabel="Help" onClick={() => { navigate('/help'); setShowHelp(true); }} />
                                    <IconButton iconProps={signoutIcon} title="Sign Out" ariaLabel="Sign Out" onClick={() => instance.logoutRedirect({ postLogoutRedirectUri: "/" })} />
                                </div>
                            )}
                        </AuthenticatedTemplate>
                    </div>
                </Container>
            </Navbar>
            <AuthenticatedTemplate>
                {((!isGameLoaded) && (!isBanned) && (errorMessage === '')) && <div className="mt-4"><Spinner size={SpinnerSize.large} label="Loading game ..." /></div>}
                {(isGameComplete && !isBoardComplete && !showHelp) && ( 
                    <GameComplete gameInfo={user!.currentGame} token={accessToken} setUser={setUser} />
                )}
                {(isGameLoaded && (!isGameComplete || (isGameComplete && isBoardComplete)) && !showHelp) && ( 
                    <div>
                        <BoardSteps gameInfo={user?.currentGame} />
                        <Alert className="mt-2" variant='danger' show={(errorMessage !== '')}>{errorMessage}</Alert>
                        <Grid gameInfo={user?.currentGame} currentGuess={currentGuess} currentRowClass={currentRowClass} />
                    </div>
                )}
                {(!isGameLoaded) && (                        
                    <Alert className="mt-2" variant='danger' show={(errorMessage !== '')}>{errorMessage}</Alert>
                )}
                {(isBanned) && (<div>
                        <h2>This account has been banned for malicious activity.</h2>
                        <p className="mt-4">If you believe this ban was issued in error, please contact <a href="mailto:support@moddigital.net">support@moddigital.net</a> to appeal.</p>
                    </div>)}
                {(isBoardComplete && isWordFound && !showHelp) && (
                    <div className="h4 mt-4 mb-2 text-center">{WIN_MESSAGES[Math.floor(Math.random() * WIN_MESSAGES.length)]}</div>
                )}
                {(isBoardComplete && !showHelp) && (
                    <div className="mb-4 w-100 text-center">{points}</div>
                )}
                {(isBoardComplete && !isGameComplete && !showHelp) && (
                    <div className="mb-4 w-100 text-center">
                        <button className="btn btn-primary" onClick={onNextBoard}>Next Board</button>
                    </div>
                )}
                {(isGameLoaded && !showHelp && !isBoardComplete && !isGameComplete) && <Keyboard
                    onChar={onChar}
                    onDelete={onDelete}
                    onEnter={onEnter}
                    isRevealing={user?.currentGame?.isRevealing}
                    gridSize={gridSize}
                    result={user?.currentGame?.boards[user?.currentGame?.currentBoard] ?? null}
                />}
                {(showHelp) && (
                    <div className="container">
                        <Help />
                        <div className="text-center"><button id="closeHelp" className="btn btn-primary" onClick={() => {navigate('/'); setShowHelp(false); }}>Close</button></div>
                    </div>
                )}
                {(isGameComplete && isBoardComplete) && <div className="mt-4"><Spinner size={SpinnerSize.large} label="Completing game ..." /></div>}
            </AuthenticatedTemplate>
            <UnauthenticatedTemplate>
                <Help />
                <div className="container">
                    <hr />
                    <p className="h3 mt-4">PLAY NOW</p>
                    <p>Use your <b>Facebook</b>, <b>Google</b>, <b>Microsoft</b>, or <b>Twitter</b> social accounts to sign in to play. This allows us to provide cross-device saves, game statistics, and leaderboards.</p>
                    <p>If you prefer not to use a social account, you can instead create an account directly with Words 5X using only your email address.</p>
                    <button id="signin" className="btn btn-primary" onClick={() => instance.loginRedirect(loginRequest)}>Play Now! Sign in or Create Account</button>
                </div>
            </UnauthenticatedTemplate>
        </div>
    </div>
    );
};